import { useEffect, useRef } from 'react';
import { LoadDropdown, useInputHelper } from 'framework/form';
import type { DefaultDropdownProps, Option } from 'framework/form';
import { POSITION_PROFILE_STATUSES } from '../../constants';
import type { PositionProfileListItem } from '../../types';
import usePositionProfilesService from '../../usePositionProfilesService';

type PositionProfileDropdownProps = Omit<DefaultDropdownProps, 'options'> & {
  specializationId?: string | number | null;
  technologyId?: string | null;
  competencyLevel?: string | null;
};

type PositionProfileOptionValue = Pick<
  PositionProfileListItem,
  'id' | 'specializationId' | 'technologyId' | 'competencyLevel'
>;

type PositionDefinition = {
  specializationId?: string | number | null;
  technologyId?: string | null;
  competencyLevel?: string | null;
};

const isSamePositionDefinition = (left: PositionDefinition, right: PositionDefinition) =>
  String(left.specializationId ?? '') === String(right.specializationId ?? '') &&
  (left.technologyId ?? null) === (right.technologyId ?? null) &&
  left.competencyLevel === right.competencyLevel;

const PositionProfileDropdownWithDeps = ({
  specializationId,
  technologyId,
  competencyLevel,
  ...props
}: PositionProfileDropdownProps) => {
  const { search } = usePositionProfilesService();
  const { getRawOnChangeCallback, getSource } = useInputHelper(props);
  const currentDefinition: PositionDefinition = { specializationId, technologyId, competencyLevel };
  const previousDefinitionRef = useRef(currentDefinition);
  const selectedDefinitionRef = useRef<PositionDefinition | null>(null);

  useEffect(() => {
    const previousDefinition = previousDefinitionRef.current;
    previousDefinitionRef.current = currentDefinition;

    if (isSamePositionDefinition(previousDefinition, currentDefinition)) return;

    if (selectedDefinitionRef.current && isSamePositionDefinition(selectedDefinitionRef.current, currentDefinition)) {
      selectedDefinitionRef.current = null;
      return;
    }

    selectedDefinitionRef.current = null;
    getRawOnChangeCallback()?.({ [getSource()]: null });
    // Input helper methods and the definition object are recreated on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specializationId, technologyId, competencyLevel]);

  const handleChange = (values: Record<string, unknown>) => {
    const source = getSource();
    const selectedProfile = values[source] as PositionProfileOptionValue | undefined;
    const onChangeCallback = getRawOnChangeCallback();

    if (!onChangeCallback) return;

    if (!selectedProfile || typeof selectedProfile !== 'object') {
      selectedDefinitionRef.current = null;
      onChangeCallback({ [source]: values[source] });
      return;
    }

    selectedDefinitionRef.current = selectedProfile;

    onChangeCallback({
      [source]: selectedProfile.id,
      specializationId: selectedProfile.specializationId,
      technologyId: selectedProfile.technologyId,
      competencyLevel: selectedProfile.competencyLevel,
    });
  };

  const loadPositionProfiles = () => {
    if (!specializationId) {
      return Promise.resolve({ result: [] });
    }

    return search({
      pageSize: 100,
      pageNumber: 0,
      filter: {
        status: { in: [POSITION_PROFILE_STATUSES.ACTIVE, POSITION_PROFILE_STATUSES.DEPRECATED] },
        specializationId: { eq: Number(specializationId) },
        ...(technologyId ? { technologyId: { eq: technologyId } } : {}),
        ...(competencyLevel ? { competencyLevel: { eq: competencyLevel } } : {}),
      },
      sort: { name: 'ASC' },
    });
  };

  return (
    <LoadDropdown
      {...props}
      loadDataMethod={loadPositionProfiles}
      mapResults={(profile: PositionProfileListItem) => ({
        value: {
          id: profile.id,
          specializationId: profile.specializationId,
          technologyId: profile.technologyId,
          competencyLevel: profile.competencyLevel,
        },
        disabled: profile.status === POSITION_PROFILE_STATUSES.DEPRECATED,
        label: profile.name,
      })}
      getOptionValue={(option: Option) => String((option.value as PositionProfileOptionValue).id)}
      onChangeCallback={handleChange}
      placeholder="Select position profile"
      deps={[specializationId, technologyId, competencyLevel]}
    />
  );
};

export default PositionProfileDropdownWithDeps;
